import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { songsApi } from '@/services/api/songs';
import { albumsApi } from '@/services/api/albums';
import { useAuth } from '@/context/AuthContext';
import { useAudioPlayer } from '@/context/AudioPlayerContext';
import { useAppData } from '@/context/AppDataContext';
import { appDataStore } from '@/services/cache/appDataStore';
import { audioCache } from '@/services/cache/audioCache';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import CoverArt from '@/components/common/CoverArt';
import AlbumScopeSelector from '@/components/albums/AlbumScopeSelector';
import AlbumCover from '@/components/albums/AlbumCover';
import { Song } from '@/types/song';
import { Album } from '@/types/album';
import { formatTimeAgo } from '@/utils/formatters';
import { getAutoCachePlayback, setAutoCachePlayback } from '@/utils/preferences';
import { Play, LogOut, Menu, X, List, Download, Trash2, Pin, RefreshCw, DownloadCloud, Check } from 'lucide-react';

interface SongListRowProps {
  song: Song;
  onPlay: () => void;
  isPlaying: boolean;
}

/** Spotify-style album header: cover art, metadata, and a Play button. */
function AlbumHeader({
  album,
  onPlay,
  disabled,
}: {
  album: Album;
  onPlay: () => void;
  disabled: boolean;
}) {
  const meta = [album.artist, album.year, `${album.songCount ?? 0} song${album.songCount === 1 ? '' : 's'}`]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="mb-8 flex flex-col items-center text-center">
      <AlbumCover
        albumId={album.albumId}
        coverArtUrl={album.coverArtUrl}
        alt={album.albumName}
        className="w-48 h-48 object-cover rounded-lg shadow-lg mb-4"
      />
      <h2 className="text-2xl font-bold text-white">{album.albumName}</h2>
      <p className="text-text-muted text-sm mt-1">{meta}</p>
      <button
        onClick={onPlay}
        disabled={disabled}
        className="mt-4 flex items-center gap-2 bg-primary text-black font-semibold rounded-full px-7 py-2.5 hover:bg-primary-hover transition-colors disabled:opacity-50"
        aria-label="Play album"
      >
        <Play size={18} fill="currentColor" />
        Play
      </button>
    </div>
  );
}

/** Album track row — no artwork (album art lives in the header), track number instead. */
function AlbumTrackRow({
  song,
  index,
  onPlay,
  isPlaying,
}: {
  song: Song;
  index: number;
  onPlay: () => void;
  isPlaying: boolean;
}) {
  return (
    <div className="group py-2" style={{ paddingLeft: '4px' }}>
      <div className="flex items-center gap-3">
        <span className="w-6 text-center text-sm text-text-muted tabular-nums flex-shrink-0">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <Link to={`/songs/${song.songId}`} className="block min-w-0">
            <h3 className="text-base font-normal leading-tight text-white hover:text-primary transition-colors truncate">
              {song.songName}
            </h3>
          </Link>
          <div className="flex items-center gap-3 text-sm text-text-muted mt-1" style={{ lineHeight: '1rem' }}>
            <span>{formatTimeAgo(song.lastUpdated, true)}</span>
            {(song.revisionCount ?? 0) > 0 && (
              <span className="flex items-center gap-1">
                {song.pinnedRevisionName && <Pin size={12} className="flex-shrink-0" />}
                {song.revisionCount} revision{song.revisionCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onPlay}
          className="flex items-center justify-center w-8 h-8 rounded-full bg-surface text-white hover:bg-border transition-colors flex-shrink-0"
          disabled={isPlaying}
          aria-label={isPlaying ? 'Playing' : 'Play'}
        >
          <Play size={14} fill="currentColor" className="ml-0.5" />
        </button>
      </div>
    </div>
  );
}

function SongListRow({ song, onPlay, isPlaying }: SongListRowProps) {
  // The cover slot is always rendered (own art, album fallback, or a
  // placeholder box) so titles line up consistently — matching the desktop app.
  return (
    <div className="group py-2" style={{ paddingLeft: '4px' }}>
      <div className="flex items-center gap-3">
        <Link to={`/songs/${song.songId}`} className="flex-shrink-0">
          <CoverArt
            songId={song.songId}
            coverArtUrl={song.coverArtUrl}
            alt={song.songName}
            className="w-11 h-11 object-cover rounded"
          />
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/songs/${song.songId}`} className="block min-w-0">
            <h3 className="text-base font-normal leading-tight text-white hover:text-primary transition-colors truncate">
              {song.songName}
            </h3>
          </Link>
          <div className="flex items-center gap-3 text-sm text-text-muted mt-1" style={{ lineHeight: '1rem' }}>
            <span>{formatTimeAgo(song.lastUpdated, true)}</span>
            {(song.revisionCount ?? 0) > 0 && (
              <span className="flex items-center gap-1">
                {song.pinnedRevisionName && <Pin size={12} className="flex-shrink-0" />}
                {song.revisionCount} revision{song.revisionCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onPlay}
          className="flex items-center justify-center w-8 h-8 rounded-full bg-surface text-white hover:bg-border transition-colors flex-shrink-0"
          disabled={isPlaying}
          aria-label={isPlaying ? 'Playing' : 'Play'}
        >
          <Play size={14} fill="currentColor" className="ml-0.5" />
        </button>
      </div>
    </div>
  );
}

interface CacheModalState {
  isOpen: boolean;
  type: 'download' | 'delete' | null;
  progress: string;
  isComplete: boolean;
}

export default function HomePage() {
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedAlbumId, setSelectedAlbumId] = useState<number | null>(null);
  const [autoCache, setAutoCache] = useState(getAutoCachePlayback());
  const [cacheModal, setCacheModal] = useState<CacheModalState>({
    isOpen: false,
    type: null,
    progress: '',
    isComplete: false,
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const { signOut } = useAuth();
  const { playSong, playSongs, isPlaying, currentSong } = useAudioPlayer();
  const { isRefreshing } = useAppData();

  // Albums for the scope selector (cached locally; refreshed in the background).
  const { data: albums = [] } = useQuery({
    queryKey: ['albums'],
    queryFn: () => albumsApi.listAlbums(),
  });

  const selectedAlbum = albums.find((a) => a.albumId === selectedAlbumId) || null;

  // Switching scope clears any search so the album view starts clean.
  function handleSelectAlbum(albumId: number | null) {
    setSelectedAlbumId(albumId);
    setSearch('');
  }

  // The selected album may disappear after a background refresh; reset to "All".
  useEffect(() => {
    if (
      selectedAlbumId !== null &&
      albums.length > 0 &&
      !albums.some((a) => a.albumId === selectedAlbumId)
    ) {
      setSelectedAlbumId(null);
    }
  }, [albums, selectedAlbumId]);

  // When scoped to an album, the list is that album's tracks (in order),
  // otherwise the full song list. Both are filtered by the (scoped) search.
  const { data, isLoading, error } = useQuery({
    queryKey: ['songs', search, selectedAlbumId],
    queryFn: () =>
      selectedAlbumId === null
        ? songsApi.listSongs({ search, limit: 50 })
        : albumsApi.getAlbumSongs(selectedAlbumId, search).then((songs) => ({ songs })),
  });

  // Filter out hidden songs
  const songs = (data?.songs || []).filter(song => !song.hidden);

  const totalSongs = appDataStore.getAllSongs().filter((s) => !s.hidden).length;

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  async function handleDownloadSongs() {
    setMenuOpen(false);
    setCacheModal({
      isOpen: true,
      type: 'download',
      progress: 'Initializing download...',
      isComplete: false,
    });

    try {
      // Get all songs and revisions
      const allSongs = appDataStore.getAllSongs();
      console.log('[Download] Total songs from store:', allSongs.length);
      const audioUrls: { url: string; name: string }[] = [];

      // Collect all audio URLs
      for (const song of allSongs) {
        // Ensure this song's details (audioFileUrl + revisions) are loaded.
        await appDataStore.ensureSongDetails(song.songId);
        const loadedSong = appDataStore.getSong(song.songId) || song;
        const revisions = appDataStore.getRevisions(song.songId);
        console.log('[Download] Found revisions:', revisions.length);

        // Add original song audio
        if (loadedSong.audioFileUrl) {
          const audioUrlResponse = await songsApi.getAudioUrl(song.songId);
          audioUrls.push({ url: audioUrlResponse.audioUrl, name: loadedSong.songName });
          console.log('[Download] Added original song audio:', loadedSong.songName);
        }

        // Add all revision audios
        for (const revision of revisions) {
          if (revision.audioFileUrl) {
            const audioUrlResponse = await songsApi.getAudioUrl(song.songId, {
              revisionId: revision.songRevisionId,
            });
            audioUrls.push({ url: audioUrlResponse.audioUrl, name: revision.songName });
            console.log('[Download] Added revision audio:', revision.songName);
          }
        }
      }

      console.log('[Download] Total audio URLs collected:', audioUrls.length);

      setCacheModal(prev => ({ ...prev, progress: `Found ${audioUrls.length} audio files. Downloading...` }));

      let downloaded = 0;
      let skipped = 0;

      // Download each audio file
      for (const { url, name } of audioUrls) {
        const cacheKey = url.split('?')[0];

        // Check if already cached
        const cached = await audioCache.get(cacheKey);
        if (cached) {
          skipped++;
          setCacheModal(prev => ({
            ...prev,
            progress: `Downloaded: ${downloaded}, Skipped (already cached): ${skipped}, Remaining: ${audioUrls.length - downloaded - skipped}`
          }));
          continue;
        }

        // Download and cache
        try {
          const response = await fetch(url);
          if (response.ok) {
            const blob = await response.blob();
            await audioCache.set(cacheKey, blob, 4320); // 6 months
            downloaded++;
          }
        } catch (err) {
          console.error(`Failed to download ${name}:`, err);
        }

        setCacheModal(prev => ({
          ...prev,
          progress: `Downloaded: ${downloaded}, Skipped (already cached): ${skipped}, Remaining: ${audioUrls.length - downloaded - skipped}`
        }));
      }

      setCacheModal(prev => ({
        ...prev,
        progress: `Complete! Downloaded ${downloaded} new files, skipped ${skipped} already cached.`,
        isComplete: true,
      }));
    } catch (err) {
      console.error('Error downloading songs:', err);
      setCacheModal(prev => ({
        ...prev,
        progress: `Error: ${err instanceof Error ? err.message : 'Failed to download songs'}`,
        isComplete: true,
      }));
    }
  }

  async function handleDeleteCache() {
    setMenuOpen(false);
    setCacheModal({
      isOpen: true,
      type: 'delete',
      progress: 'Checking cache...',
      isComplete: false,
    });

    try {
      const count = await audioCache.getCount();
      const size = await audioCache.getSize();
      const sizeMB = (size / (1024 * 1024)).toFixed(2);

      setCacheModal(prev => ({ ...prev, progress: `Found ${count} cached files (${sizeMB} MB). Deleting...` }));

      await audioCache.clear();

      setCacheModal(prev => ({
        ...prev,
        progress: `Complete! Deleted ${count} cached files (${sizeMB} MB).`,
        isComplete: true,
      }));
    } catch (err) {
      console.error('Error deleting cache:', err);
      setCacheModal(prev => ({
        ...prev,
        progress: `Error: ${err instanceof Error ? err.message : 'Failed to delete cache'}`,
        isComplete: true,
      }));
    }
  }

  function closeModal() {
    setCacheModal({
      isOpen: false,
      type: null,
      progress: '',
      isComplete: false,
    });
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-2xl font-bold text-primary">Songlab</h1>
            {isRefreshing && (
              <span className="flex items-center gap-1.5 text-xs text-text-muted">
                <RefreshCw size={12} className="animate-spin" />
                Refreshing catalog…
              </span>
            )}
          </div>

          {/* Hamburger Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 text-text-secondary hover:text-white transition-colors"
              aria-label="Menu"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-surface border border-border rounded-lg shadow-lg overflow-hidden z-50">
                <Link
                  to="/playlists"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-text-secondary hover:bg-surface-hover hover:text-white transition-colors"
                >
                  <List size={18} />
                  Playlists
                </Link>
                <button
                  onClick={handleDownloadSongs}
                  className="w-full flex items-center gap-3 px-4 py-3 text-text-secondary hover:bg-surface-hover hover:text-white transition-colors"
                >
                  <Download size={18} />
                  Download Songs
                </button>
                <button
                  onClick={handleDeleteCache}
                  className="w-full flex items-center gap-3 px-4 py-3 text-text-secondary hover:bg-surface-hover hover:text-white transition-colors"
                >
                  <Trash2 size={18} />
                  Delete Song Cache
                </button>
                <button
                  onClick={() => {
                    const next = !autoCache;
                    setAutoCachePlayback(next);
                    setAutoCache(next);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-text-secondary hover:bg-surface-hover hover:text-white transition-colors"
                  role="switch"
                  aria-checked={autoCache}
                >
                  <DownloadCloud size={18} />
                  <span className="flex-1 text-left">Auto-cache played songs</span>
                  {autoCache && <Check size={16} className="text-primary flex-shrink-0" />}
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    signOut();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-text-secondary hover:bg-surface-hover hover:text-white transition-colors"
                >
                  <LogOut size={18} />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Album scope selector — only when one or more albums exist */}
        {albums.length > 0 && (
          <div className="mb-4">
            <AlbumScopeSelector
              albums={albums}
              selectedAlbumId={selectedAlbumId}
              onSelect={handleSelectAlbum}
              totalSongs={totalSongs}
            />
          </div>
        )}

        {/* Album header (with art + Play) when scoped to an album, else search */}
        {selectedAlbum ? (
          <AlbumHeader
            album={selectedAlbum}
            onPlay={() => playSongs(songs, 0)}
            disabled={songs.length === 0}
          />
        ) : (
          <div className="mb-6">
            <input
              type="text"
              placeholder="Search songs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input max-w-md"
            />
          </div>
        )}

        {/* Song / track list */}
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="text-center text-red-500">
            Failed to load songs. Please try again.
          </div>
        ) : songs.length === 0 ? (
          <div className="text-center text-text-secondary py-12">
            {selectedAlbum ? 'No songs in this album' : 'No songs found'}
          </div>
        ) : selectedAlbumId === null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
            {songs.map((song) => (
              <SongListRow
                key={song.songId}
                song={song}
                onPlay={() => playSong(song, undefined, true)}
                isPlaying={isPlaying && currentSong?.songId === song.songId}
              />
            ))}
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            {songs.map((song, index) => (
              <AlbumTrackRow
                key={song.songId}
                song={song}
                index={index}
                onPlay={() => playSongs(songs, index)}
                isPlaying={isPlaying && currentSong?.songId === song.songId}
              />
            ))}
          </div>
        )}
      </main>

      {/* Cache Management Modal */}
      {cacheModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-white mb-4">
              {cacheModal.type === 'download' ? 'Download Songs' : 'Delete Song Cache'}
            </h2>
            <div className="mb-6">
              <p className="text-text-secondary whitespace-pre-line">
                {cacheModal.progress}
              </p>
              {!cacheModal.isComplete && (
                <div className="mt-4">
                  <LoadingSpinner />
                </div>
              )}
            </div>
            {cacheModal.isComplete && (
              <button
                onClick={closeModal}
                className="w-full btn-primary"
              >
                OK
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
